import React from 'react';

/**
 * @interface ErrorProps
 * @description Contains ErrorBoundary props and types
 */
interface ErrorProps {
  /**
   * @field message
   * @description Message to display
   * @type {string}
   * @memberof ErrorProps
   */
  message?: string;
  /**
   * @field children
   * @description React Component to be render inside error boundary
   * @type {React.ReactNode}
   * @memberof ErrorProps
   */
  children: React.ReactNode;
}

/**
 * @interface ErrorState
 * @description Contains ErrorBoundary state and types
 */
interface ErrorState {
  /**
   *
   * @field hasError
   * @description Determines if any run error has been thrown or not
   * @type {boolean}
   * @memberof ErrorState
   */
  hasError: boolean;
}

/**
 * @export
 * @component ErrorBoundary
 * @description Catch JavaScript errors anywhere in their child component tree and display the message
 * @extends {React.Component<ErrorProps, ErrorState>}
 */
export default class ErrorBoundary extends React.Component<ErrorProps, ErrorState> {
  constructor(props: ErrorProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    // Update state so the next render can show the fallback UI.
    return { hasError: true };
  }

  render(): React.ReactNode {
    const { hasError } = this.state;
    const { message, children } = this.props;
    if (hasError) {
      // You can render any custom fallback UI
      return <h3>{message || 'Something went wrong.'}</h3>;
    }

    return children;
  }
}
